import { Injectable } from '@angular/core';
import { mergeMap, map } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ErrorMessageComponent } from 'src/app/shared/presentationals/messages/error-message/error-message.component';
import { NotificationMessageComponent } from 'src/app/shared/presentationals/messages/notification-message/notification-message.component';
import { SuccessMessageComponent } from 'src/app/shared/presentationals/messages/success-message/success-message.component';
import { DirtyComponent } from '../components/dirty.component';
import { ConfirmMessageComponent } from 'src/app/shared/presentationals/messages/confirm-message/confirm-message.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { BaseComponent } from '../components/base.component';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  constructor(private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly authenticationService: AuthenticationService) {
  }

  openError(message: string): MatSnackBarRef<ErrorMessageComponent> {
    return this.snackBar.openFromComponent(ErrorMessageComponent, {
      horizontalPosition: 'end',
      data: message
    });
  }

  openErrorLabel(label: string, params?: object): MatSnackBarRef<ErrorMessageComponent> {
    const errorMessage = this.translateService.instant(label, params);

    return this.openError(errorMessage);
  }

  openNotification(texts: [string, object][]): MatSnackBarRef<NotificationMessageComponent> {
    return this.snackBar.openFromComponent(NotificationMessageComponent, {
      horizontalPosition: 'end',
      data: texts
    });
  }

  closeStatusSnackBarMessages(): void {
    if (this.snackBar._openedSnackBarRef) {
      if (this.snackBar._openedSnackBarRef.containerInstance.snackBarConfig.duration !== 0) {
        this.snackBar.dismiss();
      }
    }
  }

  closeAllSnackBarMessages(): void {
    if (this.snackBar._openedSnackBarRef) {
      this.snackBar.dismiss();
    }
  }

  openSuccess(message: string, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {
    return this.snackBar.openFromComponent(SuccessMessageComponent, {
      horizontalPosition: 'end',
      data: message,
      duration: duration
    });
  }

  openSuccessLabel(label: string, params?: object, duration?: number): MatSnackBarRef<SuccessMessageComponent> {
    const translatedLabel = this.translateService.instant(label, params);
    return this.openSuccess(translatedLabel, duration);
  }

  openSaveSuccess(): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessLabel('messages.save-success');
  }

  openDeleteSuccess(): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessLabel('messages.delete-success');
  }

  openUnexpectedError(): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessLabel('messages.unexpected-error');
  }

  openConfirmLabel(message: string, primaryButtonLabel: string = 'common.ok', secondaryButtonLabel: string = 'common.cancel'): MatSnackBarRef<ConfirmMessageComponent> {
    return this.snackBar.openFromComponent(ConfirmMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'show-large-text',
      data: {
        message: this.translateService.instant(message),
        primaryButtonLabel: this.translateService.instant(primaryButtonLabel),
        secondaryButtonLabel: this.translateService.instant(secondaryButtonLabel),
      }
    });
  }

  openConfirm(message: string, primaryButtonLabel: string | null = null, secondaryButtonLabel: string | null = null): MatSnackBarRef<ConfirmMessageComponent> {
    return this.snackBar.openFromComponent(ConfirmMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'show-large-text',
      data: {
        message: message,
        primaryButtonLabel: primaryButtonLabel,
        secondaryButtonLabel: secondaryButtonLabel,
      }
    });
  }

  openDeleteConfirm(component: BaseComponent, onConfirm: () => void) {
    this.openDefaultConfirm(component,
      this.translateService.instant('messages.delete-confirmation'),
      this.translateService.instant('common.delete'),
      this.translateService.instant('common.cancel'),
      onConfirm);
  }

  openDefaultConfirm(component: BaseComponent,
    message: string,
    primaryButtonLabel: string | null = null,
    secondaryButtonLabel: string | null = null,
    onConfirm: () => void) {
    const snackBarRef = this.openConfirm(message, primaryButtonLabel, secondaryButtonLabel);

    snackBarRef.afterDismissed()
      .pipe(map(() => snackBarRef.instance.isConfirmed))
      .unsubscribeOnDestroy(component)
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          onConfirm();
        }
      });
  }

  openDirty(component: DirtyComponent): Observable<boolean> {
    return component.isDirty.pipe(
      mergeMap(isDirty => {
        // Only show the dirty check if the user is authenticated.
        // Otherwise it can happen that the message is shown when he is redirected to the login page.
        if (isDirty && this.authenticationService.isAuthenticated()) {
          const snackBarRef = this.openConfirmLabel(this.translateService.instant('messages.cancel-dirty-confirmation'));

          return snackBarRef
            .afterDismissed().pipe(map(() => {
              return snackBarRef.instance.isConfirmed;
            }));
        }

        return of(true);
      }));
  }
}
